import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientService } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { UpdateClientRequest } from '../types';

const ClientEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateClientRequest>({
    name: '',
    subdomain: '',
    description: ''
  });

  // Charger les données du client
  useEffect(() => {
    const fetchClient = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const client = await clientService.getClientById(id);
        
        // Récupérer les données du client
        // Note: description peut être undefined si le backend ne le renvoie pas
        setFormData({
          name: client.name || '',
          subdomain: client.subdomain || '',
          description: client.description || ''
        });
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erreur lors du chargement du client');
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClient();
  }, [id, navigate]);

  // Le sous-domaine n'est pas modifiable en mode édition, quel que soit le plan

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      await clientService.updateClient(id, formData);
      navigate(`/clients/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la modification du client');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">Chargement des données du client...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Modifier le client</h1>
      
      <Card>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Nom du client *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subdomain">
              Sous-domaine *
            </label>
            <div className="flex items-center">
              <input
                type="text"
                id="subdomain"
                name="subdomain"
                value={formData.subdomain}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
                disabled={true}
              />
              <span className="ml-2 text-gray-600">.api100.io</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Le sous-domaine est généré automatiquement à partir du nom du client
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              rows={3}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              variant="secondary"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Modification en cours...' : 'Enregistrer les modifications'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ClientEdit;
